import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, IndianRupee, TrendingUp, Share2, Layers, ChevronRight } from "lucide-react";
import { api, absoluteUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Home() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState({ total_designs: 0, this_month: 0, total_value: 0, quotes_shared: 0 });
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/designs").then((r) => setRecent((r.data || []).slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <div className="sq-label">{t("welcome")}</div>
        <h1 className="text-3xl font-heading font-bold mt-1 text-secondary">
          {user?.name || user?.business_name || user?.phone}
        </h1>
      </div>

      {/* Big CTA */}
      <button
        onClick={() => nav("/new")}
        data-testid={TID.newDesignBtn}
        className="w-full text-left relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-6 sq-tap shadow-md"
      >
        <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-primary-foreground/10" />
        <div className="absolute -right-2 -top-6 w-28 h-28 rounded-full bg-primary-foreground/10" />
        <div className="flex items-center gap-4 relative">
          <span className="w-14 h-14 rounded-xl bg-primary-foreground/15 grid place-items-center">
            <Plus size={28} strokeWidth={2.4} />
          </span>
          <div>
            <div className="text-2xl font-heading font-bold">{t("newDesign")}</div>
            <div className="text-sm text-primary-foreground/85">{t("newDesignSub")}</div>
          </div>
        </div>
      </button>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard testId={TID.statTotal} icon={<Layers size={16} />} label={t("totalDesigns")} value={stats.total_designs} />
        <StatCard testId={TID.statMonth} icon={<TrendingUp size={16} />} label={t("monthDesigns")} value={stats.this_month} />
        <StatCard testId={TID.statValue} icon={<IndianRupee size={16} />} label={t("totalValue")} value={inr(stats.total_value)} />
        <StatCard testId={TID.statShared} icon={<Share2 size={16} />} label={t("quotesShared")} value={stats.quotes_shared} />
      </div>

      {/* Recent */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-heading font-semibold">{t("recent")}</h2>
          <Link to="/orders" className="text-sm font-medium text-primary hover:underline">
            {t("orders")}
          </Link>
        </div>
        <div className="space-y-3" data-testid={TID.recentList}>
          {recent.length === 0 && (
            <div className="sq-card p-6 text-center text-muted-foreground">{t("empty")}</div>
          )}
          {recent.map((d) => (
            <button
              key={d.id}
              onClick={() => nav(`/results/${d.id}`)}
              data-testid={TID.recentItem}
              className="w-full flex items-center gap-3 sq-card p-3 hover:border-primary/40 sq-tap text-left"
            >
              <img
                src={absoluteUrl(d.image_url)}
                alt=""
                className="w-14 h-14 rounded-lg object-cover bg-muted"
                onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{d.design_name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {d.garment_type} · {d.machine_brand} · {d.fabric_type}
                </div>
              </div>
              <div className="text-right pr-1">
                <div className="font-heading font-bold text-lg">{inr(d.quote?.final_price)}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {d.quote?.delivery_date || "—"}
                </div>
              </div>
              <ChevronRight size={18} className="text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="sq-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="sq-label">{label}</span>
      </div>
      <div className="sq-stat text-3xl mt-2" data-testid={testId}>{value}</div>
    </div>
  );
}
